import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import {
  Face1,
  Face2,
  Face3,
  Face4,
  Face5,
  Face6,
  Icon,
} from 'chatie-design-system/icon';

import DottedList from './DottedList';

const boxCSS = (theme: Theme) => css`
  padding: ${Spacing.medium}px;
  border-radius: ${Radius.medium}px;
  background-color: ${theme.var.gray50};

  h5 {
    margin-bottom: ${Spacing.medium}px;
  }

  p {
    ${Typography.body14}
  }

  * + * {
    margin-top: ${Spacing.xsmall}px;
  }

  & + & {
    margin-top: ${Spacing.medium}px;
  }
`;

const RestrictionInfo = () => {
  const { t } = useTranslation();

  return (
    <>
      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face1} size={Sizes.large} />
          <span>{t('정상')}</span>
        </h5>

        <p className="multiline">
          {t('채티 서비스 정책을 잘 지키는 채티즈 😆')}
        </p>
      </div>

      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face2} size={Sizes.large} />
          <span>{t('주의')}</span>
        </h5>

        <p className="multiline">
          {t(
            '채티는 이용자 누구든지 실수할 수 있고 고의로 정책을 위반했다고 생각하지 않습니다. 첫 위반시에는 계정에 "주의" 상태를 부여합니다.'
          )}
        </p>
        <p className="multiline">
          {t('"주의" 상태는 7일 동안 계정에 유지됩니다.')}
        </p>
        <p className="multiline">
          {t('"주의" 상태는 신규 콘텐츠 발행에 영향을 주지 않습니다.')}
        </p>
      </div>

      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face3} size={Sizes.large} />
          <span>{t('1차 경고')}</span>
        </h5>

        <p className="multiline">
          {t(
            '채티 계정에 "주의" 상태가 유지되는 동안, 다시 정책을 위반하게 되면 "1차 경고" 상태를 부여합니다.'
          )}
        </p>

        <p className="multiline">
          {t('"1차 경고" 상태는 30일 동안 유지됩니다.')}
        </p>

        <p className="multiline">
          {t(
            '"1차 경고" 상태는 3일 동안 신규 콘텐츠 발행이 중지되며 아래와 같은 작업을 할 수 없습니다.'
          )}
        </p>

        <DottedList
          items={[
            t('작품 수정'),
            t('회차 발행 (비공개 → 공개)'),
            t('커뮤니티 게시글 생성 및 수정'),
            t('댓글 작성'),
            t('답글 작성'),
          ]}
        />

        <p className="multiline">
          {t(
            '3일이 지나면 신규 콘텐츠 발행 권한을 다시 회복하지만, 30일 동안 "1차 경고" 상태는 유지됩니다.'
          )}
        </p>
      </div>

      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face4} size={Sizes.large} />
          <span>{t('2차 경고')}</span>
        </h5>

        <p className="multiline">
          {t(
            '채티 계정에 "1차 경고" 상태가 유지되는 동안, 다시 정책을 위반하게 되면 "2차 경고" 상태를 부여합니다.'
          )}
        </p>

        <p className="multiline">
          {t('"2차 경고" 상태는 30일 동안 유지됩니다.')}
        </p>

        <p className="multiline">
          {t(
            '"2차 경고" 상태는 7일 동안 신규 콘텐츠 발행이 중지되며 아래와 같은 작업을 할 수 없습니다.'
          )}
        </p>

        <DottedList
          items={[
            t('작품 수정'),
            t('회차 발행 (비공개 → 공개)'),
            t('커뮤니티 게시글 생성 및 수정'),
            t('댓글 작성'),
            t('답글 작성'),
          ]}
        />

        <p className="multiline">
          {t(
            '7일이 지나면 신규 콘텐츠 발행 권한을 다시 회복하지만, 30일 동안 "2차 경고" 상태는 유지됩니다.'
          )}
        </p>
      </div>

      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face5} size={Sizes.large} />
          <span>{t('3차 경고')}</span>
        </h5>

        <p className="multiline">
          {t(
            '채티 계정에 "2차 경고" 상태가 유지되는 동안, 다시 정책을 위반하게 되면 "3차 경고" 상태를 부여합니다.'
          )}
        </p>

        <p className="multiline">
          {t('"3차 경고" 상태는 30일 동안 유지됩니다.')}
        </p>

        <p className="multiline">
          {t(
            '"3차 경고" 상태는 30일 동안 신규 콘텐츠 발행이 중지되며 아래와 같은 작업을 할 수 없습니다.'
          )}
        </p>

        <DottedList
          items={[
            t('작품 수정'),
            t('회차 발행 (비공개 → 공개)'),
            t('커뮤니티 게시글 생성 및 수정'),
            t('댓글 작성'),
            t('답글 작성'),
          ]}
        />

        <p className="multiline">
          {t(
            '30일이 지나면 신규 콘텐츠 발행 권한을 다시 회복하지만, 30일 동안 "3차 경고" 상태는 유지됩니다.'
          )}
        </p>
      </div>

      <div css={boxCSS}>
        <h5 className="icon-text">
          <Icon icon={Face6} size={Sizes.large} />
          <span>{t('4차 경고')}</span>
        </h5>

        <p className="multiline">
          {t(
            '채티 계정에 "3차 경고" 상태가 유지되는 동안, 다시 정책을 위반하게 되면 "4차 경고" 상태를 부여합니다.'
          )}
        </p>
        <p className="multiline">
          {t('4차 경고를 받으면 계정이 즉시 정지 됩니다.')}
        </p>
      </div>
    </>
  );
};

export default RestrictionInfo;
